import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Building2, Users, CalendarCheck, FileText, Banknote, Star, Crown } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUSD = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

/** /provider/multi-location — Enterprise tier feature (Blueprint #22). */
export default function MultiLocationDashboard() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/provider/multi-location`, getAuthHeader());
        setData(d);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        setError(detail || 'Could not load multi-location dashboard');
        toast.error(detail || 'Could not load dashboard');
      } finally {
        setLoading(false);
      }
    })();
  }, [getAuthHeader]);

  return (
    <DashboardShell data-testid="multi-location-page">
      <header className="mb-6">
        <div className="cn-eyebrow inline-flex items-center gap-1.5">
          <Building2 className="w-3 h-3" /> Enterprise tier · Multi-location
        </div>
        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">All your locations at a glance</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Census, visits, claims, and family NPS across every branch under your parent organization — last 30 days.
        </p>
      </header>

      {loading ? (
        <div className="rounded-2xl bg-white border border-slate-100 p-8 text-slate-500">Loading locations…</div>
      ) : error ? (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-6 max-w-xl" data-testid="multi-loc-error">
          <h2 className="font-bold text-amber-900">Enterprise tier required</h2>
          <p className="text-sm text-amber-800 mt-2">{error}</p>
          <p className="text-xs text-amber-700 mt-3">
            Multi-location aggregation is included in the $18 PMPM Enterprise tier. Talk to sales to upgrade.
          </p>
        </div>
      ) : data && (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="multi-loc-totals">
            <Tile label="Locations" value={data.location_count} icon={<Building2 className="w-4 h-4 text-cn-primary" />} testid="tile-loc-count" />
            <Tile label="Active patients" value={Number(data.totals.active_patients).toLocaleString()} icon={<Users className="w-4 h-4 text-emerald-600" />} testid="tile-active-patients" />
            <Tile label="Visits (30d)" value={Number(data.totals.visits_completed_30d).toLocaleString()} icon={<CalendarCheck className="w-4 h-4 text-blue-600" />} testid="tile-visits" />
            <Tile label="Collected (30d)" value={fmtUSD(data.totals.collected_30d_usd)} icon={<Banknote className="w-4 h-4 text-emerald-700" />} testid="tile-collected" />
            <Tile label="NPS avg" value={data.totals.nps_avg != null ? data.totals.nps_avg : '—'} icon={<Star className="w-4 h-4 text-amber-500" />} testid="tile-nps" />
          </div>

          {/* Locations grid */}
          <section className="rounded-2xl bg-white border border-slate-100 overflow-hidden" data-testid="multi-loc-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left p-3 font-semibold">Location</th>
                  <th className="text-right p-3 font-semibold">Patients</th>
                  <th className="text-right p-3 font-semibold">Visits (30d)</th>
                  <th className="text-right p-3 font-semibold">Open claims</th>
                  <th className="text-right p-3 font-semibold">Collected (30d)</th>
                  <th className="text-right p-3 font-semibold">NPS</th>
                </tr>
              </thead>
              <tbody>
                {data.locations.map((loc) => (
                  <tr key={loc.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`multi-loc-row-${loc.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {loc.is_parent && <Crown className="w-3.5 h-3.5 text-amber-500" />}
                        <div>
                          <div className="font-semibold text-slate-900">{loc.display_name}</div>
                          {(loc.city || loc.state) && <div className="text-xs text-slate-500">{[loc.city, loc.state].filter(Boolean).join(', ')}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-right tabular-nums">{loc.active_patients}</td>
                    <td className="p-3 text-right tabular-nums">{loc.visits_completed_30d}</td>
                    <td className="p-3 text-right tabular-nums">{loc.open_claims}</td>
                    <td className="p-3 text-right tabular-nums font-semibold">{fmtUSD(loc.collected_30d_usd)}</td>
                    <td className="p-3 text-right tabular-nums">{loc.nps_avg != null ? loc.nps_avg : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <p className="mt-4 text-[10px] text-slate-400">Generated {new Date(data.generated_at).toLocaleString()}</p>
        </>
      )}
    </DashboardShell>
  );
}

function Tile({ label, value, icon, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
        {icon}
      </div>
      <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}
